import React from "react"
import { EnvelopeIcon, LinkIcon } from "@heroicons/react/24/outline"
import { showSuccessToast } from "aldoo-ra/Toast"
import useT from "../hooks/useT"
import { getImageURL } from "../utils/images"
import { sanitizeText } from "../utils/pageMeta"

const XIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
)
const PinterestIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M12 2C6.48 2 2 6.48 2 12c0 4.24 2.64 7.86 6.36 9.32-.09-.79-.17-2.01.03-2.87.18-.78 1.18-4.98 1.18-4.98s-.3-.6-.3-1.49c0-1.4.81-2.44 1.82-2.44.86 0 1.27.64 1.27 1.42 0 .86-.55 2.15-.83 3.35-.24 1 .5 1.82 1.49 1.82 1.79 0 3.16-1.88 3.16-4.6 0-2.41-1.73-4.09-4.2-4.09-2.86 0-4.54 2.15-4.54 4.36 0 .86.33 1.79.75 2.29.08.1.09.19.07.29-.08.32-.25 1-.28 1.14-.04.19-.15.23-.34.14-1.25-.58-2.03-2.4-2.03-3.87 0-3.15 2.29-6.04 6.6-6.04 3.46 0 6.16 2.47 6.16 5.77 0 3.44-2.17 6.21-5.18 6.21-1.01 0-1.96-.53-2.29-1.15l-.62 2.37c-.23.87-.84 1.96-1.25 2.62.94.29 1.94.45 2.97.45 5.52 0 10-4.48 10-10S17.52 2 12 2z" />
  </svg>
)
const FacebookIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M22 12.06C22 6.5 17.52 2 12 2S2 6.5 2 12.06c0 5.02 3.66 9.18 8.44 9.94v-7.03H7.9v-2.91h2.54V9.85c0-2.52 1.49-3.91 3.78-3.91 1.09 0 2.24.2 2.24.2v2.47h-1.26c-1.24 0-1.63.78-1.63 1.57v1.88h2.78l-.45 2.91h-2.33V22c4.78-.76 8.44-4.92 8.44-9.94z" />
  </svg>
)

// Email / Pinterest / Facebook / X / copy-link share row
export default function ProductSharePanel({ product, className = "" }) {
  const t = useT()
  const url = () => window.location.href

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url())
    } catch {
      const area = document.createElement("textarea")
      area.value = url()
      area.style.position = "fixed"
      area.style.left = "-9999px"
      document.body.appendChild(area)
      area.select()
      document.execCommand("copy")
      document.body.removeChild(area)
    }
    showSuccessToast(t("link_copied_to_clipboard") === "link_copied_to_clipboard" ? "Link copied to clipboard" : t("link_copied_to_clipboard"), "", { duration: 2000 })
  }

  const shareViaEmail = () => {
    const subject = encodeURIComponent(product.name || t("share_email_subject"))
    const body = encodeURIComponent(`${t("share_email_message")}:\n\n${product.name}\n${url()}`)
    window.location.href = `mailto:?subject=${subject}&body=${body}`
  }

  const popup = (href, size) => window.open(href, "_blank", size)
  const shareOnPinterest = () => {
    const image = getImageURL(product.square || product.thumb)
    const media = image ? `&media=${encodeURIComponent(image)}` : ""
    popup(`https://pinterest.com/pin/create/button/?url=${encodeURIComponent(url())}${media}&description=${encodeURIComponent(product.name || "")}`, "width=750,height=600")
  }
  const shareOnFacebook = () => {
    const name = sanitizeText(product.name, 100)
    const desc = product.description ? sanitizeText(product.description, 200) : ""
    popup(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url())}&quote=${encodeURIComponent(desc ? `${name} - ${desc}` : name)}`, "width=600,height=400")
  }
  const shareOnX = () =>
    popup(`https://twitter.com/intent/tweet?url=${encodeURIComponent(url())}&text=${encodeURIComponent(product.name || t("share_x_text"))}`, "width=550,height=420")

  const btn = "inline-flex items-center justify-center w-10 h-10 rounded-full hover:bg-neutral transition-colors"
  return (
    <div className={`pt-4 border-t border-bordered ${className}`}>
      <div className="flex flex-row items-center justify-center gap-4">
        <button type="button" onClick={shareViaEmail} className={`${btn} text-text_secondary hover:text-text`} aria-label={t("share_tip_email")}>
          <EnvelopeIcon className="w-6 h-6" />
        </button>
        <button type="button" onClick={shareOnPinterest} className={`${btn} text-[#E60023]`} aria-label={t("share_tip_pinterest")}>
          <PinterestIcon className="w-6 h-6" />
        </button>
        <button type="button" onClick={shareOnFacebook} className={`${btn} text-[#1877F2]`} aria-label={t("share_tip_facebook")}>
          <FacebookIcon className="w-6 h-6" />
        </button>
        <button type="button" onClick={shareOnX} className={`${btn} text-text`} aria-label={t("share_tip_x")}>
          <XIcon className="w-5 h-5" />
        </button>
        <button type="button" onClick={copyLink} className={`${btn} text-text_secondary hover:text-text`} aria-label={t("share_tip_copy_link")}>
          <LinkIcon className="w-6 h-6" />
        </button>
      </div>
    </div>
  )
}
